import React, { useState, useEffect } from 'react';
import { X, Search, Plus } from 'lucide-react';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

interface PetOwner {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  pets: Pet[];
}

interface WalkInAppointmentModalProps {
  onClose: () => void;
  onSuccess: (appointment?: any) => void;
}

const APPOINTMENT_TYPES = [
  { value: 'checkup', label: 'Checkup' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'consultation', label: 'Consultation' },
];

export const WalkInAppointmentModal: React.FC<WalkInAppointmentModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [petOwners, setPetOwners] = useState<PetOwner[]>([]);
  const [filteredOwners, setFilteredOwners] = useState<PetOwner[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<PetOwner | null>(null);
  const [selectedPetIds, setSelectedPetIds] = useState<string[]>([]);
  const [appointmentType, setAppointmentType] = useState('checkup');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(onClose, true);

  useEffect(() => {
    if (user?.practice_id) {
      fetchPetOwners();
    }
  }, [user?.practice_id]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = petOwners.filter((owner) =>
        owner.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        owner.phone?.includes(searchTerm) ||
        owner.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOwners(filtered);
    } else {
      setFilteredOwners(petOwners);
    }
  }, [searchTerm, petOwners]);

  const fetchPetOwners = async () => {
    if (!user?.practice_id) return;
    
    setLoadingOwners(true);
    try {
      // Use the same pattern as other components - the authUtils interceptor will add the token
      const response = await fetch(API_ENDPOINTS.PET_OWNERS.LIST);

      if (!response.ok) {
        throw new Error('Failed to fetch pet owners');
      }

      const data = await response.json();
      
      // Map the response to match our interface
      const mappedOwners = data.map((owner: any) => ({
        id: owner.uuid,
        full_name: owner.full_name,
        email: owner.email,
        phone: owner.phone,
        pets: owner.pets?.map((pet: any) => ({
          id: pet.uuid,
          name: pet.name,
          species: pet.species,
          breed: pet.breed,
        })) || [],
      }));
      
      setPetOwners(mappedOwners);
      setFilteredOwners(mappedOwners);
    } catch (err) {
      console.error('Error fetching pet owners:', err);
      setError('Failed to load pet owners');
    } finally {
      setLoadingOwners(false);
    }
  };

  const handlePetToggle = (petId: string) => {
    setSelectedPetIds((prev) =>
      prev.includes(petId)
        ? prev.filter((id) => id !== petId)
        : [...prev, petId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedOwner) {
      setError('Please select a pet owner');
      return;
    }

    if (selectedPetIds.length === 0) {
      setError('Please select at least one pet');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the API endpoint - authUtils will add the token
      // Create appointment for today at current time
      const now = new Date();
      const selectedPets = selectedOwner.pets.filter(p => selectedPetIds.includes(p.id));
      const petNames = selectedPets.map(p => p.name).join(', ');
      const appointmentTypeLabel = appointmentType.charAt(0).toUpperCase() + appointmentType.slice(1);
      
      const appointmentData = {
        practice_id: user?.practice_id,
        pet_owner_id: selectedOwner.id,
        pet_ids: selectedPetIds,
        appointment_date: now.toISOString(),
        duration_minutes: 30,
        appointment_type: appointmentType,
        title: `${petNames} ${appointmentTypeLabel} - Walk-in`,
        description: description || undefined,
        status: 'scheduled', // Will be in waitlist
        // No assigned_vet_user_id - walk-ins are not assigned initially
      };

      const response = await fetch(API_ENDPOINTS.APPOINTMENTS.CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create appointment');
      }

      const createdAppointment = await response.json();
      onSuccess(createdAppointment);
      onClose();
    } catch (err) {
      console.error('Error creating walk-in appointment:', err);
      setError(err instanceof Error ? err.message : 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[500] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Create Walk-in Appointment</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Step 1: Select Pet Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Pet Owner <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, phone, or email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                disabled={loadingOwners || !!selectedOwner}
              />
            </div>

            {loadingOwners ? (
              <div className="mt-2 text-sm text-gray-500">Loading pet owners...</div>
            ) : !selectedOwner && searchTerm && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                {filteredOwners.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 text-center">
                    No pet owners found
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredOwners.map((owner) => (
                      <button
                        key={owner.id}
                        type="button"
                        onClick={() => {
                          setSelectedOwner(owner);
                          setSelectedPetIds([]);
                          setSearchTerm('');
                        }}
                        className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{owner.full_name}</div>
                        {(owner.phone || owner.email) && (
                          <div className="text-sm text-gray-500">
                            {owner.phone && <span>{owner.phone}</span>}
                            {owner.phone && owner.email && <span className="mx-2">•</span>}
                            {owner.email && <span>{owner.email}</span>}
                          </div>
                        )}
                        {owner.pets.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {owner.pets.length} pet{owner.pets.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedOwner && (
              <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{selectedOwner.full_name}</div>
                    {selectedOwner.phone && (
                      <div className="text-sm text-gray-600">{selectedOwner.phone}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedOwner(null);
                      setSelectedPetIds([]);
                    }}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Change
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Select Pets */}
          {selectedOwner && selectedOwner.pets.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Pet(s) <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {selectedOwner.pets.map((pet) => (
                  <label
                    key={pet.id}
                    className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPetIds.includes(pet.id)}
                      onChange={() => handlePetToggle(pet.id)}
                      className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">{pet.name}</div>
                      <div className="text-sm text-gray-500">
                        {pet.species} {pet.breed && `• ${pet.breed}`}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Appointment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Appointment Type <span className="text-red-500">*</span>
            </label>
            <select
              value={appointmentType}
              onChange={(e) => setAppointmentType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              required
            >
              {APPOINTMENT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Step 4: Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Visit
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the visit reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              rows={3}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !selectedOwner || selectedPetIds.length === 0}
          >
            {loading ? 'Creating...' : 'Create Walk-in'}
          </button>
        </div>
      </div>
    </div>
  );
};

